import "@fullcalendar/react/dist/vdom";
import MDBox from "components/MDBox";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import CalendarContext from "modules/jobs/JobsSchedule/CalendarContext";
import { useState, useCallback, useRef } from "react";
import { DateRangeProvider } from "modules/jobs/JobsSchedule/DateRangeContext";
import { omit } from "ramda";
import { v4 as uuidv4 } from "uuid";
import JobsListSidebar from "./components/JobsListSidebar";
import CalendarSection from "./components/CalendarSection";
import useJobsTableData from "hooks/jobs/useJobsTableData";
import PageTour, { PageTourHandle } from "components/PageTour/PageTour";
import TourHelpButton from "components/PageTour/TourHelpButton";
import jobsSchedulePageTourSteps from "./JobsSchedulePage.tour";
import { Step } from "react-joyride";

export default function JobsSchedulePage() {
  const tourRef = useRef<PageTourHandle>(null);
  const activeTourStepTargetRef = useRef<Step["target"] | null>(null);
  const [selectedCities, setSelectedCities] = useState<string[]>(() => {
    const savedFilters = JSON.parse(localStorage.getItem("JobsSchedulesFilters") || "{}");
    return savedFilters.cities || [];
  });
  const [view, setView] = useState<"calendar" | "map">("calendar");
  const jobsTableData = useJobsTableData({
    jobIds: undefined,
    cities: selectedCities,
    queryPageSize: view === "map" ? 200 : undefined,
  });

  const [calendarsToUpdate, setCalendarsToUpdate] = useState<Record<string, () => void>>({});

  const subscribeCalendarToUpdates = useCallback(
    (callback: () => void) => {
      const id = uuidv4();
      setCalendarsToUpdate((prevState) => ({
        ...prevState,
        [id]: callback,
      }));
      return id;
    },
    [setCalendarsToUpdate]
  );

  const unsubscribeCalendarFromUpdates = useCallback(
    (key: string) => {
      setCalendarsToUpdate((prevState) => omit([key])(prevState));
    },
    [setCalendarsToUpdate]
  );

  const triggerCalendarUpdate = useCallback(() => {
    Object.values(calendarsToUpdate).forEach((callback) => {
      if (typeof callback === "function") {
        callback();
      }
    });
  }, [calendarsToUpdate]);

  return (
    <DashboardPage
      renderNavbar={true}
      showBreadcrumbs={true}
      showNavbarOnAllScreens={true}
      layoutPadding={0}
      breadcrumbAction={
        <TourHelpButton
          tooltip="Click here to learn more about Scheduling."
          onClick={() => tourRef.current?.replay()}
        />
      }
    >
      <DateRangeProvider>
        <CalendarContext.Provider
          value={{
            subscribeCalendarToUpdates,
            unsubscribeCalendarFromUpdates,
            triggerCalendarUpdate,
          }}
        >
          <PageTour
            ref={tourRef}
            tourKey="jobsSchedulePage"
            steps={jobsSchedulePageTourSteps}
            onStepChange={(step) => {
              activeTourStepTargetRef.current = step.target;
            }}
          />
          <MDBox
            sx={{
              display: "flex",
              height: "100vh",
              overflow: "hidden",
            }}
          >
            {view === "calendar" && (
              <MDBox
                sx={{
                  width: { xs: "280px", sm: "320px", md: "400px" },
                  flexShrink: 0,
                  overflow: "hidden",
                }}
              >
                <JobsListSidebar
                  jobsTableData={jobsTableData}
                  selectedCities={selectedCities}
                  onCitiesChange={setSelectedCities}
                  view={view}
                  onViewChange={setView}
                  onPhaseClick={() => {
                    // Only the ".tour-job-phase-row" step advances via this click; the
                    // schedule modal's own steps open/close it via before-hooks instead,
                    // so re-clicking the row while those steps are active must not
                    // double-advance the tour.
                    if (activeTourStepTargetRef.current === ".tour-job-phase-row") {
                      tourRef.current?.next();
                    }
                  }}
                />
              </MDBox>
            )}

            <MDBox sx={{ flex: 1, minWidth: 0, width: "100%", overflow: "hidden" }}>
              <CalendarSection
                jobs={jobsTableData.data || []}
                jobsLoading={jobsTableData.loading}
                selectedProductTypes={jobsTableData.organizationProductTypeIds}
                selectedJobStatuses={jobsTableData.filtering.statuses as any}
                onJobStatusesChange={jobsTableData.filtering.setStatuses}
                onProductTypeChange={jobsTableData.setOrganizationProductTypeIds}
                view={view}
                onViewChange={setView}
              />
            </MDBox>
          </MDBox>
        </CalendarContext.Provider>
      </DateRangeProvider>
    </DashboardPage>
  );
}
